import { InfoCircleOutlined } from '@ant-design/icons';
import { Form, FormInstance, Popover, Select } from 'antd';
import { isBoolean, isEmpty } from 'lodash';
import React from 'react';
import {
  CHARACTER_AGE_OPTIONS,
  NUDITY_DETECTED_OPTIONS,
  NUDITY_EXTENT_OPTIONS,
  NUDITY_TYPE_OPTIONS,
  POWER_IMBALANCE_OPTIONS,
  SEXUAL_ACTS_DETECTED_OPTIONS,
  SEXUAL_RELATIONSHIP_TYPE_OPTIONS,
  TONE_OPTIONS,
  TRUE_FALSE_OPTIONS,
} from '../../../../../common/constants';
import CaseInsensitiveSelect, {
  toTitleCase,
} from '../../../../../components/CaseInsensitiveSelect';
import { VoteEnum } from '../../../../../types/common.type';
import { SexuallyInappropriateAnalysesData } from '../SexuallyInappropriateAnalyses';
import {
  characterAgePopoverContent,
  euphemismsListPopoverContent,
  excessiveFocusOnBodyDescriptionPopoverContent,
  languageOfSecrecyOrConcealmentPopoverContent,
  nudityDetectedPopoverContent,
  nudityExtentPopoverContent,
  nudityTypePopoverContent,
  powerImbalancePresentPopoverContent,
  roleplayOrFantasyContextPopoverContent,
  sexualActsDetectedPopoverContent,
  sexualRelationshipTypePopoverContent,
  suggestivePhrasesListPopoverContent,
  tonePopoverContent,
  voilenceOverlapPopoverContent,
} from './SuggestiveDetectionDetails';

export type SuggestiveContentFormValues = {
  sexuallySexualRelationshipType?: string[];
  sexuallyCharacterAge?: string[];
  sexuallyEuphemismsList?: string[];
  sexuallySuggestivePhrasesList?: string[];
  sexuallyPowerImbalancePresent?: boolean | null;
  sexuallyTone?: string[];
  sexuallyExcessiveFocusOnBodyDescription?: boolean | null;
  sexuallyLanguageOfSecrecyOrConcealment?: boolean | null;
  sexuallyRoleplayOrFantasyContext?: boolean | null;
  sexuallyViolenceOverlap?: boolean | null;
  sexuallyNudityDetected?: string[];
  sexuallyNudityExtent?: string[];
  sexuallyNudityType?: string[];
  sexuallySexualActsDetected?: string[];
  vote?: VoteEnum;
  sexuallyClientComments?: string;
  sexuallyFirstPersonPerspective?: boolean | null;
  sexuallyPhysicalContactType?: string[] | null;
  sexuallySetting?: string[] | null;
  sexuallyExplicitTermsFound?: boolean | null;
  sexuallyExplicitTermsList?: string[] | null;
  sexuallyAnatomicalTermsPresent?: boolean | null;
  sexuallyAnatomicalTermsList?: string[] | null;
  sexuallyContainsDialogue?: boolean | null;
  sexuallyChromosomallyDefinedGender?: string[] | null;
  sexuallySelfIdentifiedGenders?: string[] | null;
  sexuallyFinalRating?: number | null;
  sexuallyRatingReason?: string | null;
};

const infoIcon = (content: React.ReactNode) => (
  <Popover content={content} placement="top" overlayClassName="field-content">
    <InfoCircleOutlined className="signal-detection-info-icon" />
  </Popover>
);

const SuggestiveContentForm: React.FC<{
  form: FormInstance;
  setIsSubmitBtnDisabled: React.Dispatch<React.SetStateAction<boolean>>;
  selectSexuallyInappropriateData:
    | SexuallyInappropriateAnalysesData
    | undefined;
}> = ({ form, setIsSubmitBtnDisabled, selectSexuallyInappropriateData }) => {
  const feedbackData = selectSexuallyInappropriateData?.subSection?.feedback;
  return (
    <Form
      layout="vertical"
      form={form}
      className="signal-detection-form-grid"
      initialValues={{}}
      onValuesChange={() => setIsSubmitBtnDisabled(false)}
    >
      <div className="signal-detection-form-item signal-detection-form-item">
        <Form.Item
          label={
            <span className="d-flex align-center">
              Sexual Relationship Type{' '}
              {infoIcon(sexualRelationshipTypePopoverContent)}
            </span>
          }
          name="sexuallySexualRelationshipType"
          className={
            !isEmpty(feedbackData?.sexuallySexualRelationshipType)
              ? 'signal-detection-form-item--with-below-text'
              : 'signal-detection-form-item'
          }
        >
          <CaseInsensitiveSelect
            mode="tags"
            size="middle"
            options={SEXUAL_RELATIONSHIP_TYPE_OPTIONS}
            placeholder="Select sexual relationship type"
          />
        </Form.Item>
        {!(
          JSON.stringify(feedbackData?.sexuallySexualRelationshipType) ===
          JSON.stringify(
            selectSexuallyInappropriateData?.sexualRelationshipType,
          )
        ) && (
          <div className="signal-detection-below-text">
            <span className="font-w-600">Originally:</span>{' '}
            <span>
              {(selectSexuallyInappropriateData?.sexualRelationshipType || [])
                .map((val: string) => toTitleCase(val))
                .join(', ') || '--'}
            </span>
          </div>
        )}
      </div>
      <div className="signal-detection-form-item signal-detection-form-item">
        <Form.Item
          label={
            <span className="d-flex align-center">
              Character Age {infoIcon(characterAgePopoverContent)}
            </span>
          }
          name="sexuallyCharacterAge"
          className={
            !isEmpty(feedbackData?.sexuallyCharacterAge)
              ? 'signal-detection-form-item--with-below-text'
              : 'signal-detection-form-item'
          }
        >
          <CaseInsensitiveSelect
            mode="tags"
            size="middle"
            options={CHARACTER_AGE_OPTIONS}
            placeholder="Select character age"
          />
        </Form.Item>
        {!(
          JSON.stringify(feedbackData?.sexuallyCharacterAge) ===
          JSON.stringify(selectSexuallyInappropriateData?.characterAge)
        ) && (
          <div className="signal-detection-below-text">
            <span className="font-w-600">Originally:</span>{' '}
            <span>
              {(selectSexuallyInappropriateData?.characterAge || [])
                .map((val: string) => toTitleCase(val))
                .join(', ') || '--'}
            </span>
          </div>
        )}
      </div>
      <div className="signal-detection-form-item signal-detection-form-item">
        <Form.Item
          label={
            <span className="d-flex align-center">
              Euphemisms List {infoIcon(euphemismsListPopoverContent)}
            </span>
          }
          name="sexuallyEuphemismsList"
          className={
            !isEmpty(feedbackData?.sexuallyEuphemismsList)
              ? 'signal-detection-form-item--with-below-text'
              : 'signal-detection-form-item'
          }
        >
          <CaseInsensitiveSelect
            mode="tags"
            size="middle"
            options={[]}
            placeholder="Select euphemisms"
          />
        </Form.Item>
        {!(
          JSON.stringify(feedbackData?.sexuallyEuphemismsList) ===
          JSON.stringify(selectSexuallyInappropriateData?.euphemismsList)
        ) && (
          <div className="signal-detection-below-text">
            <span className="font-w-600">Originally:</span>{' '}
            <span>
              {(selectSexuallyInappropriateData?.euphemismsList || [])
                .map((val: string) => toTitleCase(val))
                .join(', ') || '--'}
            </span>
          </div>
        )}
      </div>
      <div className="signal-detection-form-item signal-detection-form-item">
        <Form.Item
          label={
            <span className="d-flex align-center">
              Suggestive Phrases List{' '}
              {infoIcon(suggestivePhrasesListPopoverContent)}
            </span>
          }
          name="sexuallySuggestivePhrasesList"
          className={
            !isEmpty(feedbackData?.sexuallySuggestivePhrasesList)
              ? 'signal-detection-form-item--with-below-text'
              : 'signal-detection-form-item'
          }
        >
          <CaseInsensitiveSelect
            mode="tags"
            size="middle"
            options={[]}
            placeholder="Select suggestive phrases"
          />
        </Form.Item>
        {!(
          JSON.stringify(feedbackData?.sexuallySuggestivePhrasesList) ===
          JSON.stringify(selectSexuallyInappropriateData?.suggestivePhrasesList)
        ) && (
          <div className="signal-detection-below-text">
            <span className="font-w-600">Originally:</span>{' '}
            <span>
              {(selectSexuallyInappropriateData?.suggestivePhrasesList || [])
                .map((val: string) => toTitleCase(val))
                .join(', ') || '--'}
            </span>
          </div>
        )}
      </div>
      <div className="signal-detection-form-item signal-detection-form-item">
        <Form.Item
          label={
            <span className="d-flex align-center">
              Power Imbalance Present{' '}
              {infoIcon(powerImbalancePresentPopoverContent)}
            </span>
          }
          name="sexuallyPowerImbalancePresent"
          className={
            isBoolean(feedbackData?.sexuallyPowerImbalancePresent)
              ? 'signal-detection-form-item--with-below-text'
              : 'signal-detection-form-item'
          }
        >
          <Select
            size="middle"
            options={POWER_IMBALANCE_OPTIONS}
            placeholder="Select power imbalance"
          />
        </Form.Item>
        {isBoolean(feedbackData?.sexuallyPowerImbalancePresent) &&
          isBoolean(selectSexuallyInappropriateData?.powerImbalancePresent) &&
          !(
            feedbackData?.sexuallyPowerImbalancePresent ===
            selectSexuallyInappropriateData?.powerImbalancePresent
          ) && (
            <div className="signal-detection-below-text">
              <span className="font-w-600">Originally:</span>{' '}
              <span>
                {selectSexuallyInappropriateData?.powerImbalancePresent === true
                  ? 'True'
                  : selectSexuallyInappropriateData?.powerImbalancePresent ===
                      false
                    ? 'False'
                    : ''}
              </span>
            </div>
          )}
      </div>
      <div className="signal-detection-form-item signal-detection-form-item">
        <Form.Item
          label={
            <span className="d-flex align-center">
              Tone {infoIcon(tonePopoverContent)}
            </span>
          }
          name="sexuallyTone"
          className={
            !isEmpty(feedbackData?.sexuallyTone)
              ? 'signal-detection-form-item--with-below-text'
              : 'signal-detection-form-item'
          }
        >
          <CaseInsensitiveSelect
            mode="tags"
            size="middle"
            options={TONE_OPTIONS}
            placeholder="Select tone"
          />
        </Form.Item>
        {!(
          JSON.stringify(feedbackData?.sexuallyTone) ===
          JSON.stringify(selectSexuallyInappropriateData?.tone)
        ) && (
          <div className="signal-detection-below-text">
            <span className="font-w-600">Originally:</span>{' '}
            <span>
              {(selectSexuallyInappropriateData?.tone || [])
                .map((val: string) => toTitleCase(val))
                .join(', ') || '--'}
            </span>
          </div>
        )}
      </div>
      <div className="signal-detection-form-item signal-detection-form-item">
        <Form.Item
          label={
            <span className="d-flex align-center">
              Excessive Focus On Body Description{' '}
              {infoIcon(excessiveFocusOnBodyDescriptionPopoverContent)}
            </span>
          }
          name="sexuallyExcessiveFocusOnBodyDescription"
          className={
            isBoolean(feedbackData?.sexuallyExcessiveFocusOnBodyDescription)
              ? 'signal-detection-form-item--with-below-text'
              : 'signal-detection-form-item'
          }
        >
          <Select
            size="middle"
            options={TRUE_FALSE_OPTIONS}
            placeholder="Select option"
          />
        </Form.Item>
        {isBoolean(feedbackData?.sexuallyExcessiveFocusOnBodyDescription) &&
          isBoolean(
            selectSexuallyInappropriateData?.excessiveFocusOnBodyDescription,
          ) &&
          !(
            feedbackData?.sexuallyExcessiveFocusOnBodyDescription ===
            selectSexuallyInappropriateData?.excessiveFocusOnBodyDescription
          ) && (
            <div className="signal-detection-below-text">
              <span className="font-w-600">Originally:</span>{' '}
              <span>
                {selectSexuallyInappropriateData?.excessiveFocusOnBodyDescription ===
                true
                  ? 'True'
                  : selectSexuallyInappropriateData?.excessiveFocusOnBodyDescription ===
                      false
                    ? 'False'
                    : ''}
              </span>
            </div>
          )}
      </div>
      <div className="signal-detection-form-item signal-detection-form-item">
        <Form.Item
          label={
            <span className="d-flex align-center">
              Language of Secrecy or Concealment{' '}
              {infoIcon(languageOfSecrecyOrConcealmentPopoverContent)}
            </span>
          }
          name="sexuallyLanguageOfSecrecyOrConcealment"
          className={
            isBoolean(feedbackData?.sexuallyLanguageOfSecrecyOrConcealment)
              ? 'signal-detection-form-item--with-below-text'
              : 'signal-detection-form-item'
          }
        >
          <Select
            size="middle"
            options={TRUE_FALSE_OPTIONS}
            placeholder="Select option"
          />
        </Form.Item>
        {isBoolean(feedbackData?.sexuallyLanguageOfSecrecyOrConcealment) &&
          isBoolean(
            selectSexuallyInappropriateData?.languageOfSecrecyOrConcealment,
          ) &&
          !(
            feedbackData?.sexuallyLanguageOfSecrecyOrConcealment ===
            selectSexuallyInappropriateData?.languageOfSecrecyOrConcealment
          ) && (
            <div className="signal-detection-below-text">
              <span className="font-w-600">Originally:</span>{' '}
              <span>
                {selectSexuallyInappropriateData?.languageOfSecrecyOrConcealment ===
                true
                  ? 'True'
                  : selectSexuallyInappropriateData?.languageOfSecrecyOrConcealment ===
                      false
                    ? 'False'
                    : ''}
              </span>
            </div>
          )}
      </div>
      <div className="signal-detection-form-item signal-detection-form-item">
        <Form.Item
          label={
            <span className="d-flex align-center">
              Roleplay or Fantasy Context{' '}
              {infoIcon(roleplayOrFantasyContextPopoverContent)}
            </span>
          }
          name="sexuallyRoleplayOrFantasyContext"
          className={
            isBoolean(feedbackData?.sexuallyRoleplayOrFantasyContext)
              ? 'signal-detection-form-item--with-below-text'
              : 'signal-detection-form-item'
          }
        >
          <Select
            size="middle"
            options={TRUE_FALSE_OPTIONS}
            placeholder="Select option"
          />
        </Form.Item>
        {isBoolean(feedbackData?.sexuallyRoleplayOrFantasyContext) &&
          isBoolean(
            selectSexuallyInappropriateData?.roleplayOrFantasyContext,
          ) &&
          !(
            feedbackData?.sexuallyRoleplayOrFantasyContext ===
            selectSexuallyInappropriateData?.roleplayOrFantasyContext
          ) && (
            <div className="signal-detection-below-text">
              <span className="font-w-600">Originally:</span>{' '}
              <span>
                {selectSexuallyInappropriateData?.roleplayOrFantasyContext ===
                true
                  ? 'True'
                  : selectSexuallyInappropriateData?.roleplayOrFantasyContext ===
                      false
                    ? 'False'
                    : ''}
              </span>
            </div>
          )}
      </div>
      <div className="signal-detection-form-item signal-detection-form-item">
        <Form.Item
          label={
            <span className="d-flex align-center">
              Voilence Overlap {infoIcon(voilenceOverlapPopoverContent)}
            </span>
          }
          name="sexuallyViolenceOverlap"
          className={
            isBoolean(feedbackData?.sexuallyViolenceOverlap)
              ? 'signal-detection-form-item--with-below-text'
              : 'signal-detection-form-item'
          }
        >
          <Select
            size="middle"
            options={TRUE_FALSE_OPTIONS}
            placeholder="Select option"
          />
        </Form.Item>
        {isBoolean(feedbackData?.sexuallyViolenceOverlap) &&
          isBoolean(selectSexuallyInappropriateData?.violenceOverlap) &&
          !(
            feedbackData?.sexuallyViolenceOverlap ===
            selectSexuallyInappropriateData?.violenceOverlap
          ) && (
            <div className="signal-detection-below-text">
              <span className="font-w-600">Originally:</span>{' '}
              <span>
                {selectSexuallyInappropriateData?.violenceOverlap === true
                  ? 'True'
                  : selectSexuallyInappropriateData?.violenceOverlap === false
                    ? 'False'
                    : ''}
              </span>
            </div>
          )}
      </div>
      <div className="signal-detection-form-item signal-detection-form-item">
        <Form.Item
          label={
            <span className="d-flex align-center">
              Nudity Detected {infoIcon(nudityDetectedPopoverContent)}
            </span>
          }
          name="sexuallyNudityDetected"
          className={
            !isEmpty(feedbackData?.sexuallyNudityDetected)
              ? 'signal-detection-form-item--with-below-text'
              : 'signal-detection-form-item'
          }
        >
          <CaseInsensitiveSelect
            mode="tags"
            size="middle"
            options={NUDITY_DETECTED_OPTIONS}
            placeholder="Select nudity detected"
          />
        </Form.Item>
        {!(
          JSON.stringify(feedbackData?.sexuallyNudityDetected) ===
          JSON.stringify(selectSexuallyInappropriateData?.nudityDetected)
        ) && (
          <div className="signal-detection-below-text">
            <span className="font-w-600">Originally:</span>{' '}
            <span>
              {(selectSexuallyInappropriateData?.nudityDetected || [])
                .map((val: string) => toTitleCase(val))
                .join(', ') || '--'}
            </span>
          </div>
        )}
      </div>
      <div className="signal-detection-form-item signal-detection-form-item">
        <Form.Item
          label={
            <span className="d-flex align-center">
              Nudity Extent {infoIcon(nudityExtentPopoverContent)}
            </span>
          }
          name="sexuallyNudityExtent"
          className={
            !isEmpty(feedbackData?.sexuallyNudityExtent)
              ? 'signal-detection-form-item--with-below-text'
              : 'signal-detection-form-item'
          }
        >
          <CaseInsensitiveSelect
            mode="tags"
            size="middle"
            options={NUDITY_EXTENT_OPTIONS}
            placeholder="Select nudity extent"
          />
        </Form.Item>
        {!(
          JSON.stringify(feedbackData?.sexuallyNudityExtent) ===
          JSON.stringify(selectSexuallyInappropriateData?.nudityExtent)
        ) && (
          <div className="signal-detection-below-text">
            <span className="font-w-600">Originally:</span>{' '}
            <span>
              {(selectSexuallyInappropriateData?.nudityExtent || [])
                .map((val: string) => toTitleCase(val))
                .join(', ') || '--'}
            </span>
          </div>
        )}
      </div>
      <div className="signal-detection-form-item signal-detection-form-item">
        <Form.Item
          label={
            <span className="d-flex align-center">
              Nudity Type {infoIcon(nudityTypePopoverContent)}
            </span>
          }
          name="sexuallyNudityType"
          className={
            !isEmpty(feedbackData?.sexuallyNudityType)
              ? 'signal-detection-form-item--with-below-text'
              : 'signal-detection-form-item'
          }
        >
          <CaseInsensitiveSelect
            mode="tags"
            size="middle"
            options={NUDITY_TYPE_OPTIONS}
            placeholder="Select nudity type"
          />
        </Form.Item>
        {!(
          JSON.stringify(feedbackData?.sexuallyNudityType) ===
          JSON.stringify(selectSexuallyInappropriateData?.nudityType)
        ) && (
          <div className="signal-detection-below-text">
            <span className="font-w-600">Originally:</span>{' '}
            <span>
              {(selectSexuallyInappropriateData?.nudityType || [])
                .map((val: string) => toTitleCase(val))
                .join(', ') || '--'}
            </span>
          </div>
        )}
      </div>
      <div className="signal-detection-form-item signal-detection-form-item">
        <Form.Item
          label={
            <span className="d-flex align-center">
              Sexual Acts Detected {infoIcon(sexualActsDetectedPopoverContent)}
            </span>
          }
          name="sexuallySexualActsDetected"
          className={
            !isEmpty(feedbackData?.sexuallySexualActsDetected)
              ? 'signal-detection-form-item--with-below-text'
              : 'signal-detection-form-item'
          }
        >
          <CaseInsensitiveSelect
            mode="tags"
            size="middle"
            options={SEXUAL_ACTS_DETECTED_OPTIONS}
            placeholder="Select sexual acts detected"
          />
        </Form.Item>
        {!(
          JSON.stringify(feedbackData?.sexuallySexualActsDetected) ===
          JSON.stringify(selectSexuallyInappropriateData?.sexualActsDetected)
        ) && (
          <div className="signal-detection-below-text">
            <span className="font-w-600">Originally:</span>{' '}
            <span>
              {(selectSexuallyInappropriateData?.sexualActsDetected || [])
                .map((val: string) => toTitleCase(val))
                .join(', ') || '--'}
            </span>
          </div>
        )}
      </div>
    </Form>
  );
};

export default SuggestiveContentForm;
